import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { SUGGESTED_EMOJI } from '../lib/emoji'

interface Props {
  value: string
  onChange: (emoji: string) => void
  label: string
}

/**
 * A small button showing the emoji (or a dash), which opens the palette in a
 * popover. For rows: the item editor has room for the whole grid inline, a
 * bulk-add row or a starter-pantry row does not.
 *
 * The popover is portalled to <body> and positioned from the button's screen
 * rect. Inside the row it would be clipped by the list's overflow and the
 * modal's, and a clipped popover looks exactly like a button that does
 * nothing.
 */
export function EmojiPick({ value, onChange, label }: Props) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const pop = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const r = btn.current?.getBoundingClientRect()
      if (!r) return
      const width = Math.min(320, window.innerWidth - 24)
      const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12))
      // Below the button when it fits, above it otherwise.
      const height = 240
      const below = r.bottom + 4
      const top = below + height <= window.innerHeight ? below : Math.max(8, r.top - 4 - height)
      setPos({ top, left, width })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (!btn.current?.contains(t) && !pop.current?.contains(t)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Ours to close; the modal behind stays open.
        e.stopImmediatePropagation()
        setOpen(false)
      }
    }
    // A scroll elsewhere moves the button out from under the popover, so close
    // rather than float; a scroll inside the popover is just the grid scrolling.
    const onScroll = (e: Event) => {
      if (pop.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  const pick = (e: string) => {
    onChange(e)
    setOpen(false)
    btn.current?.focus()
  }

  return (
    <>
      <button
        ref={btn}
        type="button"
        className="emoji-btn emoji-pick"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        {value || <span className="muted">–</span>}
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={pop}
            className="emoji-pop"
            role="dialog"
            aria-label={label}
            style={{ top: pos.top, left: pos.left, width: pos.width }}
          >
            <div className="emoji-grid">
              {SUGGESTED_EMOJI.map((e) => (
                <button key={e} type="button" className="emoji-btn" aria-pressed={value === e} onClick={() => pick(value === e ? '' : e)}>
                  {e}
                </button>
              ))}
            </div>
            {value && (
              <button type="button" className="linklike" onClick={() => pick('')}>
                No emoji
              </button>
            )}
          </div>,
          document.body,
        )}
    </>
  )
}
